import React from 'react'
import { Alert, Box, Button } from '@mui/material'

import {
  backgroundProblemHoldsCamera,
  type BackgroundProblem,
} from '../services/liveKitVideoBackground'
import {
  BACKGROUND_PROBLEM_ACTION_LABELS,
  backgroundProblemActions,
  backgroundProblemMessage,
  type BackgroundProblemAction,
} from '../services/backgroundProblemCopy'

export interface BackgroundProblemNoticeProps {
  problem: BackgroundProblem
  onAction: (action: BackgroundProblemAction) => void
  /** Actions this surface cannot offer, such as "Pick another" inside the picker itself. */
  hiddenActions?: BackgroundProblemAction[]
  /**
   * The id of the element that already says what is wrong, on the preview
   * beside this notice. The notice then shows only the actions, described by
   * those words, and leaves announcing them to that element: the same
   * sentence twice, side by side, read as two problems.
   */
  messageShownAt?: string
}

/**
 * Why the selected background is not showing, beside the preview and the
 * picker on the Join screen (SC-158), with what the user can do about it.
 *
 * A held camera is a warning — the user joins with the camera off unless they
 * act. A slow device or a failed switch is information: the call still works.
 */
export const BackgroundProblemNotice: React.FC<BackgroundProblemNoticeProps> = ({
  problem,
  onAction,
  hiddenActions = [],
  messageShownAt,
}) => {
  const actions = backgroundProblemActions(problem).filter(
    (action) => !hiddenActions.includes(action)
  )
  const actionsOnly = messageShownAt !== undefined
  // Nothing of its own to say, and nothing to offer: the preview has it all.
  if (actionsOnly && actions.length === 0) return null
  return (
    <Alert
      severity={backgroundProblemHoldsCamera(problem) ? 'warning' : 'info'}
      data-testid="background-problem-notice"
      data-cause={problem.cause}
      // The words are announced where they are shown; actions alone are a group.
      role={actionsOnly ? 'group' : 'alert'}
      aria-describedby={messageShownAt}
      sx={{ mt: 1.5, ...(actionsOnly && { alignItems: 'center' }) }}
    >
      {!actionsOnly && backgroundProblemMessage(problem)}
      {actions.length > 0 && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: actionsOnly ? 0 : 1 }}>
          {actions.map((action) => (
            <Button
              key={action}
              size="small"
              variant="outlined"
              color="inherit"
              onClick={() => onAction(action)}
              aria-describedby={messageShownAt}
              data-testid={`background-problem-action-${action}`}
              sx={{ textTransform: 'none' }}
            >
              {BACKGROUND_PROBLEM_ACTION_LABELS[action]}
            </Button>
          ))}
        </Box>
      )}
    </Alert>
  )
}

export default BackgroundProblemNotice
